import { useOnboardingStepsCompleted } from "@/api";
import NumerousLogo from "@/components/logo/NumerousLogo";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyH1 } from "@/components/typography/H1";
import { TypographyH3 } from "@/components/typography/H3";
import { TypographyMuted } from "@/components/typography/Muted";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { ExternalLink, Plane } from "lucide-react";
import { CreateAppStep } from "./CreateAppStep";
import { CreateSubscriptionOfferStep } from "./CreateSubscriptionOfferStep";
import { InviteUserStep } from "./InviteUserStep";
import { OnboardingCompleted } from "./OnboardingCompleted";
import { OnboardingProgress } from "./OnboardingProgress";
import { OnboardingStep } from "./OnboardingStep";
import ToggleOnboarding from "./ToggleOnboarding";
import { UploadCodeStep } from "./UploadCodeStep";

const OnboardingPage = () => {
  const {
    data,
    loading,
    error,
    numberOfStepsCompleted,
    completedPercentage,
    isOnboardingCompleted
  } = useOnboardingStepsCompleted();

  if (error) console.error(error);

  return (
    <div className="w-full mt-24">
      <TypographyH1 className="flex items-center gap-x-3 dark:text-brand-gray-50 mb-4">
        Welcome to <NumerousLogo type="brandmark" size="lg" /> Numerous 👋
      </TypographyH1>
      <TypographyBase className="col-start-1 dark:text-brand-gray-100 mb-8">
        Let&apos;s get started with a few steps to get the most from the
        Numerous platform:
      </TypographyBase>
      <div className="flex gap-4 justify-between items-end">
        {!isOnboardingCompleted && (
          <>
            <OnboardingProgress
              numberOfCompleted={numberOfStepsCompleted}
              completedPercentage={completedPercentage}
            />
            <div className="justify-end">
              <ToggleOnboarding />
            </div>
          </>
        )}
      </div>
      <div className="flex gap-x-10 mt-6">
        <div className="w-2/3 flex flex-col gap-4">
          {!loading && !error && (
            <>
              {isOnboardingCompleted && <OnboardingCompleted />}
              <OnboardingStep
                title="Sign-up"
                icon={<Plane size={18} />}
                isCompleted
              />
              <CreateAppStep isCompleted={!!data?.CreatedApp} />
              <UploadCodeStep isCompleted={!!data?.UploadedCode} />
              <InviteUserStep isCompleted={!!data?.InvitedUser} />
              <CreateSubscriptionOfferStep
                isCompleted={!!data?.CreatedSubscriptionOffer}
              />
            </>
          )}
          {error && (
            <Alert
              title="Unable to load onboarding progress"
              description="An error occurred while loading your onboarding progress. Please try to refresh the page or report the issue if it persists."
            />
          )}
          {loading && (
            <div className="text-sm text-muted-foreground flex gap-2 items-center text-nowrap">
              <LoadingCircle />
              Loading status
            </div>
          )}
        </div>

        <div className="w-1/3 h-fit p-6 rounded-md bg-white dark:bg-brand-gray-900 dark:text-brand-gray-100">
          <TypographyH3 className="dark:text-brand-gray-100 mb-4">
            Documentation
          </TypographyH3>
          <div className="space-y-6 divide-y dark:divide-brand-gray-800">
            <div>
              <TypographyBase className="font-semibold mb-0.5">
                Introduction
              </TypographyBase>
              <TypographyMuted>
                For developers new to Numerous. Learn the basics and explore the
                documentation.
              </TypographyMuted>
              <a
                href="https://www.numerous.com/docs"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="mt-4">
                  View Docs
                  <ExternalLink size={14} className="ml-1" />
                </Button>
              </a>
            </div>
            <div>
              <TypographyBase className="mt-6 font-semibold mb-0.5">
                Numerous CLI
              </TypographyBase>
              <TypographyMuted>
                Interact with Numerous from the terminal in your favorite
                development environment. Manage your apps and deployments.
              </TypographyMuted>
              <a
                href="https://www.numerous.com/docs/quick-start"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="mt-4">
                  Quick Start
                  <ExternalLink size={14} className="ml-1" />
                </Button>
              </a>
            </div>
            <div>
              <TypographyBase className="mt-6 font-semibold mb-0.5">
                Support
              </TypographyBase>
              <TypographyMuted>
                Need further assistance? Please write us directly at{" "}
                <a
                  href="mailto:support@numerous.com"
                  className="text-brand-gray-950 dark:text-brand-gray-100 underline-offset-4 hover:underline"
                >
                  support@numerous.com
                </a>
                .
              </TypographyMuted>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
